import { type PropsWithChildren, memo, type RefObject } from "react";

import { Video } from "@shopify/hydrogen-react";

import { Heading } from "@components/Heading";
import { HtmlContent } from "@components/HtmlContent";
import { Icon } from "@components/Icon";
import { Image } from "@components/Image";
import { Link } from "@components/Link";
import { Text } from "@components/Text";
import { baseStyles } from "@react/sections/BentoBox/BentoBoxCard/BentoBoxCard.styles";
import {
  type BentoBoxCardAlignX,
  type BentoBoxCardAlignY,
  type BentoBoxCardBackgroundColor,
  type BentoBoxCardContentWidthPercentage,
  type BentoBoxCardTextColor,
  type BentoBoxCardTitleSize,
} from "@root/tablescape/state/bentoBox";
import { isValidUrl } from "@root/tablescape/utilities/general";
import { tw, type TTailwindString } from "classnames";

const styles = {
  base: ({
    alignX,
    alignY,
    backgroundColor,
    className,
  }: {
    alignX: BentoBoxCardAlignX;
    alignY: BentoBoxCardAlignY;
    backgroundColor: BentoBoxCardBackgroundColor;
    className?: TTailwindString;
  }) =>
    tw(baseStyles.base({ alignX, alignY, className }), {
      [tw("bg-brick-900")]: backgroundColor === "brick",
      [tw("bg-interactive-char-pressed")]: backgroundColor === "char",
      [tw("bg-sage-900")]: backgroundColor === "sage",
      [tw("bg-blue-700")]: backgroundColor === "blue",
      [tw("bg-bone-300")]: backgroundColor === "bone",
    }),
  background: tw(
    "absolute",
    "w-full",
    "h-full",
    "top-0",
    "left-0",
    "object-cover"
  ),
  content: ({
    alignX,
    contentWidthPercentage,
    textColor,
  }: {
    alignX: BentoBoxCardAlignX;
    contentWidthPercentage: BentoBoxCardContentWidthPercentage;
    textColor: BentoBoxCardTextColor;
  }) =>
    tw(baseStyles.content({ alignX, contentWidthPercentage }), {
      [tw(`text-char-50`)]: textColor === "white",
      [tw(`text-primary`)]: textColor === "char",
    }),
  eyebrow: tw("mb-2"),
  description: tw("whitespace-pre-line"),
  videoIcon: ({ textColor }: { textColor: BentoBoxCardTextColor }) =>
    tw(
      {
        [tw(`text-char-50`)]: textColor === "white",
        [tw(`text-primary`)]: textColor === "char",
      },
      "w-10",
      "h-10",
      "lg:w-12",
      "lg:h-12"
    ),
  videoButton: tw(
    "block",
    "border-0",
    "outline-none",
    "bg-transparent",
    "cursor-pointer",
    "p-0"
  ),
  buttons: ({ alignX }: { alignX: BentoBoxCardAlignX }) =>
    tw(
      {
        [tw("relative")]: alignX === "left",
        [tw(
          "absolute",
          "top-0",
          "bottom-0",
          "right-4",
          "py-4",
          "md:right-6",
          "md:py-6"
        )]: alignX === "center",
      },
      "ml-auto",
      "flex",
      "flex-col",
      "justify-between",
      "items-end",
      "h-full"
    ),
  arrowButton: tw(
    "block",
    "self-end",
    "p-3",
    "lg:p-4.5",
    "rounded-100",
    "border",
    "border-solid",
    "border-char-900",
    "outline-none",
    "bg-char-50",
    "hover:bg-char-100",
    "active:bg-char-200",
    "cursor-pointer",
    "transition-colors",
    "duration-400",
    "ease-out",
    "mt-auto"
  ),
  arrowIcon: tw(
    "w-4",
    "h-4",
    "lg:w-5",
    "lg:h-5",
    "text-char-900",
    "flex",
    "items-center"
  ),
};

type Props = {
  alignX?: BentoBoxCardAlignX;
  alignY?: BentoBoxCardAlignY;
  backgroundColor?: BentoBoxCardBackgroundColor;
  btnLabel?: string;
  cardUrl?: string;
  className?: TTailwindString;
  contentWidthPercentage: BentoBoxCardContentWidthPercentage;
  descriptionText?: string;
  eyebrowText?: string;
  handleCtaClick?: () => void;
  handleToggleVideo: () => void;
  imageAlt?: string;
  imageUrl?: string;
  isBelowMobile: boolean;
  isVideoPlaying: boolean;
  loopVideo: boolean;
  position: number;
  textColor?: BentoBoxCardTextColor;
  titleSize?: BentoBoxCardTitleSize;
  titleText?: string;
  videoAlt?: string;
  videoRef: RefObject<HTMLVideoElement>;
  videoSources?: {
    mimeType?: string;
    url?: string;
  }[];
};

const BentoBoxCardViewBase = ({
  alignX = "left",
  alignY = "bottom",
  backgroundColor = "bone",
  btnLabel,
  cardUrl,
  className,
  contentWidthPercentage,
  descriptionText,
  eyebrowText,
  handleCtaClick,
  handleToggleVideo,
  imageAlt,
  imageUrl,
  isBelowMobile,
  isVideoPlaying,
  loopVideo,
  position,
  textColor = "char",
  titleSize = "lg",
  titleText,
  videoAlt,
  videoRef,
  videoSources,
}: PropsWithChildren<Props>) => {
  return (
    <div
      className={styles.base({ alignX, alignY, backgroundColor, className })}
    >
      {!videoSources?.length && imageUrl && (
        <Image
          alt={imageAlt}
          className={styles.background}
          src={imageUrl}
          width={isBelowMobile ? 352 : 1344}
        />
      )}
      {!!videoSources?.length && (
        <Video
          aria-label={videoAlt}
          autoPlay
          className={styles.background}
          controls={false}
          data={{
            mediaContentType: "VIDEO",
            sources: videoSources,
          }}
          {...(loopVideo && { loop: true })}
          data-chromatic="ignore"
          muted
          /**
           * @type_assertion_justification
           * TECHNICAL REASON: @shopify/hydrogen-react Video component ref type mismatch - ComponentProps<"video"> ref type doesn't align with RefObject<HTMLVideoElement> expected by videoRef
           * RUNTIME GUARANTEE: videoRef is always a RefObject<HTMLVideoElement> and the Video component renders a native <video> element, so the ref assignment is safe at runtime
           * ALTERNATIVE CONSIDERED: Attempted to properly type the ref prop, but @shopify/hydrogen-react Video component's type definitions don't properly forward refs through ComponentProps<"video">
           * RISK ASSESSMENT: Low - ref is used only for programmatic video control (play/pause) and Video component always renders native video element
           * MIGRATION_PLAN: Monitor @shopify/hydrogen-react updates for improved ref forwarding; consider contributing fix upstream
           */
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Type assertion needed due to @shopify/hydrogen-react Video component ref type mismatch
          ref={videoRef as any}
        />
      )}
      <div
        className={styles.content({
          alignX,
          contentWidthPercentage,
          textColor,
        })}
      >
        {eyebrowText && (
          <Text as="body2" className={styles.eyebrow}>
            <HtmlContent html={eyebrowText} />
          </Text>
        )}
        {titleText && (
          <Heading as="h3" size={titleSize}>
            <HtmlContent html={titleText} />
          </Heading>
        )}
        {descriptionText && (
          <Text as="body1" className={styles.description}>
            <HtmlContent html={descriptionText} />
          </Text>
        )}
      </div>
      {(!!videoSources?.length || cardUrl || handleCtaClick) && (
        <div className={styles.buttons({ alignX })}>
          {!!videoSources?.length && (
            <button
              className={styles.videoButton}
              data-mtr-as="clickModule"
              data-mtr-position={position}
              data-mtr-track="click"
              onClick={handleToggleVideo}
            >
              <Icon
                className={styles.videoIcon({ textColor })}
                name={isVideoPlaying ? "pause" : "play-circle"}
              />
              <span className={tw("sr-only")}>
                {isVideoPlaying ? "pause" : "play"}
              </span>
            </button>
          )}
          {cardUrl?.trim() &&
            isValidUrl(cardUrl.trim()) &&
            (() => {
              const trimmedUrl = cardUrl.trim();
              const isExternal =
                !trimmedUrl.startsWith("/") &&
                typeof window !== "undefined" &&
                !trimmedUrl.startsWith(window.location.origin);
              return (
                <Link
                  className={styles.arrowButton}
                  data-mtr-as="clickModule"
                  data-mtr-label={titleText}
                  data-mtr-position={position}
                  data-mtr-track="click"
                  external={isExternal}
                  to={trimmedUrl}
                >
                  <Icon className={styles.arrowIcon} name="arrow" />
                  <span className={tw("sr-only")}>
                    {btnLabel} - {titleText}
                    {isExternal ? " (opens in new tab)" : ""}
                  </span>
                </Link>
              );
            })()}
          {handleCtaClick && !cardUrl && (
            <button
              className={styles.arrowButton}
              data-mtr-as="clickModule"
              data-mtr-label={titleText}
              data-mtr-position={position}
              data-mtr-track="click"
              onClick={handleCtaClick}
            >
              <Icon className={styles.arrowIcon} name="arrow" />
              <span className={tw("sr-only")}>{btnLabel}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const BentoBoxCardView = memo(BentoBoxCardViewBase);
